import i18n from 'i18next';
import detector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import { toBcp47 } from './lib/displayNames';
import { syncMomentLocale } from './lib/momentLocale';
import de from './lang/de';
import el from './lang/el';
import en from './lang/en';
import es from './lang/es';
import fr from './lang/fr';
import hu from './lang/hu';
import it from './lang/it';
import jp from './lang/jp';
import ko from './lang/ko';
import pl from './lang/pl';
import ru from './lang/ru';
import zhchs from './lang/zh-chs';
import zhcht from './lang/zh-cht';

// EU MDR submission languages. The portal must be readable in every language of
// the member states the device software is made available in, so these are kept
// complete for the visitor-facing screens.
export const MDR_LANGUAGES = ['de', 'el', 'en', 'es', 'fr', 'hu', 'it', 'pl'];

const resources = {
  en: { translation: en },
  de: { translation: de },
  el: { translation: el },
  es: { translation: es },
  fr: { translation: fr },
  hu: { translation: hu },
  it: { translation: it },
  pl: { translation: pl },
  jp: { translation: jp },
  ru: { translation: ru },
  zhchs: { translation: zhchs },
  zhcht: { translation: zhcht },
  ko: { translation: ko },
};

export const SUPPORTED_LANGUAGES = Object.keys(resources);

// Legacy resource keys (`jp`, `zhchs`, `zhcht`) predate this file and are kept so
// visitors who already picked them keep their choice. Everything else uses the
// ISO 639-1 code, which doubles as the BCP 47 tag for `<html lang>` and for the
// Intl display-name lookups; `toBcp47` (lib/displayNames.js) owns that mapping.

i18n
  .use(detector)
  .use(initReactI18next)
  .init({
    resources,
    // No explicit `lng`: the detector picks localStorage first, then the browser
    // language, so an EU visitor lands on their own language on the first visit.
    // `load: 'languageOnly'` normalizes region tags (fr-FR -> fr) so the language
    // switcher can match `i18n.language` against the resource keys.
    load: 'languageOnly',
    supportedLngs: SUPPORTED_LANGUAGES,
    nonExplicitSupportedLngs: true,
    // Fall back to English when a key is missing in the selected language, so
    // partially-translated locales degrade to English rather than exposing raw
    // Korean-string keys. ko/en carry the full string set.
    fallbackLng: 'en',
    // The eIFU portal must set NO cookies (compliance separation). The language
    // detector defaults to caching the chosen language in BOTH localStorage and
    // a cookie, so override it to localStorage only and never read/write cookies.
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    keySeparator: '.',
    interpolation: { escapeValue: false },
  });

// Keep the document language and the date formatting in sync with the UI
// language. Screen readers and regulatory accessibility checks read
// `<html lang>`, which is otherwise stuck at the build-time value of "en".
const applyLanguage = (code) => {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', toBcp47(code));
  }
  syncMomentLocale(code);
};

applyLanguage(i18n.language);
i18n.on('languageChanged', applyLanguage);

export default i18n;
